import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { PERMISSIONS, PERMISSION_DENIED_HINT } from '@/lib/permissions'
import { toast } from 'sonner'
import * as planningApi from '../../../../lib/api/v2/planning'
import type {
  BoardLineDto, PeriodDto, PlanningReminderDto, ProjectDetailAlertDto, SupplierDto, SupplierOrderDto,
  SupplierOrderEntryDto, SupplierOrderLineDto, SupplierOrderMode,
} from '../../../../lib/api/v2/planning'
import { Reminders } from './Reminders'
import { addWorkingDays, workingDaysBetween } from '../../../../lib/working-days'
import { cn } from '../../../../lib/utils'
import { PeriodInput } from './PeriodInput'
import {
  computeStatusCounts, formatPeriod, groupLines, HOLD_KIND_LABEL, LINE_STATUS_CLASS, LINE_STATUS_LABEL,
  MODE_LABEL, ORDER_STATUS_CLASS, ORDER_STATUS_LABEL, orderEffectivePeriod, orderNextDeadline,
  periodFromInput, readiness, SUPPLIER_MODES, todayIso,
} from './supplier-orders-helpers'
import { deriveCommandesPhase } from './supplier-orders-phase'
import { supplierLineDetailOf, supplierLineTextOf } from '../client-order-helpers'

interface SupplierOrdersProps {
  projectId: string
  lines: SupplierOrderLineDto[]
  orders: SupplierOrderDto[]
  /** The explicit « rien à commander » marker (MS-03) — the C1 exit, dated. */
  nothingToOrderAt: string | null
  /** Reminders surfacing here (project + its lines + their orders) — MS-04. */
  reminders: PlanningReminderDto[]
  /** Next pose start (yyyy-MM-dd) — drives the inline C7 warning in the send/confirm forms. */
  poseStartIso: string | null
  /** Effective DeliveryBeforePoseLeadDays (null = rule off/dismissed) — fetched by the parent's
   *  load() so a rule edit in the adjacent ConstraintsBlock refreshes it (review B1). */
  deliveryLeadDays: number | null
  /** Org default for the confirmation-deadline prefill (KV SupplierConfirmDelayDays) — supplier delay wins, 5 is the last resort. */
  confirmDelayDays: number | null
  commandesAlerts: ProjectDetailAlertDto[]
  onMutated: () => void
  /** SOLY-PERM-001 — sans `orders:manage` la section se lit intégralement, aucun geste n'est offert. */
  readOnly?: boolean
}

const frDate = (iso: string) => `${iso.split('-')[2]}/${iso.split('-')[1]}`

/**
 * Supplier orders for the drawer (SOLY-PO-001 MS-02): free lines (ToOrder / OnHold with mandatory
 * deadline+context) + one card per order (Draft → Précommande → Envoyée → Confirmée → Clôturée),
 * with per-line receive (partial deliveries) and cancel (out-of-stock swaps). Orders are org-level:
 * a grouped purchase shows its org-wide line/project totals.
 */
export function SupplierOrders({ projectId, lines, orders, nothingToOrderAt, reminders, poseStartIso, deliveryLeadDays, confirmDelayDays, commandesAlerts, onMutated, readOnly = false }: SupplierOrdersProps) {
  const [mutating, setMutating] = useState(false)
  // SOLY-PERM-001 — every mutating control of this section already honours `busy`; read-only rides
  // that same channel rather than adding a second one that the next control could forget to read.
  // The « why » is stated once at the top of the section instead of on forty tooltips.
  const busy = mutating || readOnly
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [form, setForm] = useState<string | null>(null) // "order:<id>:<action>" | "line:<id>:<action>" | "bulk:*"
  const [suppliers, setSuppliers] = useState<SupplierDto[] | null>(null)

  const grouped = groupLines(lines)

  const run = async (action: () => Promise<unknown>, ok: string) => {
    if (readOnly) return // defence in depth: no control should reach here, and the API refuses anyway
    setMutating(true)
    try { await action(); toast.success(ok); setForm(null); setSelected(new Set()); onMutated() }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Action échouée') }
    finally { setMutating(false) }
  }

  const ensureSuppliers = async (): Promise<SupplierDto[]> => {
    if (suppliers) return suppliers
    const list = await planningApi.getSuppliers()
    setSuppliers(list)
    return list
  }

  // An inline-created supplier must land in THIS cache too: SendForm resolves the order's
  // supplier from it, and with silent refreshes the component never remounts to refetch —
  // a stale cache would leave the send form on « Chargement du fournisseur… » forever.
  const registerSupplier = (s: SupplierDto) =>
    setSuppliers((l) => (l ? [...l, s].sort((a, b) => a.name.localeCompare(b.name)) : l))

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const counts = computeStatusCounts(lines)
  const ready = readiness(counts)
  const registryPath = `/planning/projects/${projectId}/commandes`
  // Phase-driven progressive disclosure (mirrors Métré/Pose mini-scope 29) — the SectionCard wrapper
  // is the single collapse surface; no internal toggle.
  const phase = deriveCommandesPhase(lines, orders, nothingToOrderAt)
  const poseTail = poseStartIso ? ` du ${frDate(poseStartIso)}` : ''

  return (
    <section data-testid="planning-orders">
      {readOnly && (
        <p className="mb-2 rounded-md bg-muted px-2.5 py-1.5 text-[11px] text-muted-foreground"
          data-testid="planning-orders-readonly">
          Lecture seule — {PERMISSION_DENIED_HINT[PERMISSIONS.ORDERS_MANAGE]}
        </p>
      )}
      {commandesAlerts.length > 0 && (
        <ul className="mb-2 space-y-1">
          {commandesAlerts.map((a) => (
            <li key={a.key} className={cn('rounded-md px-2.5 py-1.5 text-[11px]',
              a.severity === 'Hard' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700')}>
              <span className="font-medium">{a.title}</span>
              {a.context && <span className="block text-muted-foreground">{a.context}</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-2">
        {/* ── a-definir : ONLY the two choices, nothing else ──────────────── */}
        {phase === 'a-definir' && (
          <div className="space-y-2 rounded-md border border-dashed p-3 text-center" data-testid="planning-orders-empty">
            <p className="text-xs text-muted-foreground">Aucun article à commander n'est défini pour ce chantier.</p>
            <Link to={registryPath} data-testid="planning-orders-registry-cta"
              className="inline-block rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
              Préparer la liste de commande →
            </Link>
            <div>
              <button type="button" disabled={busy} data-testid="planning-orders-nothing-btn"
                onClick={() => void run(() => planningApi.markNothingToOrder(projectId), '« Rien à commander » confirmé')}
                className="text-[11px] text-muted-foreground underline">
                Rien à commander pour ce chantier
              </button>
            </div>
          </div>
        )}

        {/* ── rien : the green confirmed badge + annuler only ─────────────── */}
        {phase === 'rien' && (
          <div className="flex items-center gap-2 text-xs" data-testid="planning-orders-nothing-badge">
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 font-medium text-emerald-700">
              {/* Local rendering: the marker is a UTC instant — slicing its date part would show
                  yesterday for a Brussels evening click (review I1). */}
              Rien à commander — confirmé le {new Date(nothingToOrderAt!).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })}
            </span>
            <button type="button" disabled={busy} data-testid="planning-orders-nothing-revert"
              onClick={() => void run(() => planningApi.clearNothingToOrder(projectId), 'Marqueur retiré')}
              className="text-muted-foreground underline">annuler</button>
          </div>
        )}

        {/* ── recu : a single serene line — but the registre stays reachable: a second
            intervention can need a forgotten part AFTER everything was received
            (SOLY-FINITION-001 — without this the drawer is a dead-end). ──────── */}
        {phase === 'recu' && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-emerald-700" data-testid="planning-orders-received">
              Tout reçu · prêt pour la pose{poseTail}
            </span>
            <Link to={registryPath} className="text-[11px] text-muted-foreground underline" data-testid="planning-orders-registry-link">registre</Link>
          </div>
        )}

        {/* ── en-cours : readiness + registry link + always-visible non-zero breakdown ── */}
        {phase === 'en-cours' && (
          <div className="space-y-0.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-foreground" data-testid="planning-orders-readiness">
                Pose prévue{poseTail} : {ready.received}/{ready.total} reçue{ready.total === 1 ? '' : 's'}
              </span>
              <Link to={registryPath} className="text-[11px] text-muted-foreground underline" data-testid="planning-orders-registry-link">registre</Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-1.5 pt-0.5 text-[11px] text-muted-foreground" data-testid="planning-orders-summary">
              {counts.toOrder > 0 && <span>À commander <b className="text-foreground">{counts.toOrder}</b></span>}
              {counts.onHold > 0 && <span>· En attente <b className="text-foreground">{counts.onHold}</b></span>}
              {counts.ordered > 0 && <span>· Commandée <b className="text-foreground">{counts.ordered}</b></span>}
              {counts.confirmed > 0 && <span>· Confirmée <b className="text-foreground">{counts.confirmed}</b></span>}
              {counts.received > 0 && <span>· Reçue <b className="text-foreground">{counts.received}</b></span>}
            </div>
          </div>
        )}

        {/* ── Work surface (en-cours + recu): free lines, bulk toolbar, order cards, reminders ── */}
        {(phase === 'en-cours' || phase === 'recu') && (
          <div className="space-y-2">
          {/* ── Free lines: ToOrder / OnHold ───────────────────────────── */}
          {grouped.free.length > 0 && (
            <div className="space-y-1.5">
              {/* ── Bulk toolbar (UX r2 — MS-11): always visible above the list, disabled until a line is selected ── */}
              <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-muted/60 px-2 py-1.5" data-testid="planning-bulk-toolbar">
                <label className="flex cursor-pointer items-center gap-1.5 text-[10.5px] text-foreground" data-testid="planning-bulk-select-all">
                  <input type="checkbox" className="h-3.5 w-3.5" disabled={busy}
                    checked={selected.size === grouped.free.length && grouped.free.length > 0}
                    onChange={() => {
                      if (selected.size === grouped.free.length) { setSelected(new Set()); setForm(null) }
                      else setSelected(new Set(grouped.free.map((l) => l.id)))
                    }} />
                  {selected.size > 0 ? `${selected.size} sélectionné${selected.size > 1 ? 's' : ''}` : 'Tout'}
                </label>
                <span className="text-muted-foreground/40">|</span>
                <ToolbarBtn label="Commandé…" primary active={form === 'bulk:order'} disabled={busy || selected.size === 0} testId="planning-bulk-order"
                  onClick={() => { void ensureSuppliers(); setForm(form === 'bulk:order' ? null : 'bulk:order') }} />
                <ToolbarBtn label="Grouper…" active={form === 'bulk:group'} disabled={busy || selected.size === 0} testId="planning-bulk-group"
                  onClick={() => { void ensureSuppliers(); setForm(form === 'bulk:group' ? null : 'bulk:group') }} />
                <ToolbarBtn label="Attente info…" active={form === 'bulk:info'} disabled={busy || selected.size === 0} testId="planning-bulk-info"
                  onClick={() => setForm(form === 'bulk:info' ? null : 'bulk:info')} />
                {selected.size > 0 && (
                  <button type="button" aria-label="Annuler la sélection" data-testid="planning-bulk-clear"
                    onClick={() => { setSelected(new Set()); setForm(null) }}
                    className="ml-auto px-1 text-[11px] text-muted-foreground hover:text-foreground">✕</button>
                )}
              </div>

              {/* ── Forms expand between toolbar and line list ───────────── */}
              {selected.size > 0 && form === 'bulk:order' && (
                <OrderGestureForm busy={busy} projectId={projectId} lineCount={selected.size}
                  loadSuppliers={ensureSuppliers} onSupplierCreated={registerSupplier}
                  poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} confirmDelayDays={confirmDelayDays}
                  onCancel={() => setForm(null)}
                  onSubmit={(body, extraLineIds) => run(
                    () => planningApi.createAndSendOrder({
                      ...body,
                      lineIds: [...selected, ...extraLineIds],
                    }),
                    'Commande passée')} />
              )}
              {selected.size > 0 && form === 'bulk:group' && (
                <GroupHoldForm busy={busy} lineCount={selected.size} loadSuppliers={ensureSuppliers} onSupplierCreated={registerSupplier}
                  onCancel={() => setForm(null)}
                  onSubmit={(supplierId, until, note) => run(
                    () => planningApi.bulkHoldLines({ lineIds: [...selected], kind: 'Grouping', until, note, supplierId }),
                    'Mises en attente de groupage')} />
              )}
              {selected.size > 0 && form === 'bulk:info' && (
                <InfoHoldForm busy={busy}
                  onCancel={() => setForm(null)}
                  onSubmit={(until, note) => run(
                    () => planningApi.bulkHoldLines({ lineIds: [...selected], kind: 'Info', until, note }),
                    "Demandes d'info enregistrées")} />
              )}

              {/* ── Lines below the toolbar ─────────────────────────────── */}
              {grouped.free.map((l) => (
                <FreeLine key={l.id} line={l} busy={busy} form={form} setForm={setForm}
                  selected={selected.has(l.id)} onToggleSelect={() => toggleSelect(l.id)} run={run} />
              ))}
            </div>
          )}

          {/* ── Order cards ────────────────────────────────────────────── */}
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} projectLines={grouped.byOrder.get(o.id) ?? []}
              busy={busy} mutating={mutating} form={form} setForm={setForm} run={run}
              loadSuppliers={ensureSuppliers} suppliers={suppliers}
              poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} confirmDelayDays={confirmDelayDays} />
          ))}

          {/* ── Reminders (MS-04) — only in the work phases (never a-definir/rien). ── */}
          <Reminders projectId={projectId} reminders={reminders} orders={orders} onMutated={onMutated} />

          {/* ── Cancelled free lines (history) ─────────────────────────── */}
          {grouped.cancelledFree.map((l) => (
            <div key={l.id} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`planning-order-line-${l.id}`}>
              <span className="truncate line-through">{supplierLineTextOf(l)}</span>
              <span className="flex-none text-[10px]">annulée{l.cancelReason ? ` — ${l.cancelReason}` : ''}</span>
            </div>
          ))}

          {/* « Ajouter un article » retiré du drawer (décision PO) — le registre est l'unique
              point d'entrée pour définir des lignes. */}
          </div>
        )}
      </div>
    </section>
  )
}

// ── Free line row (ToOrder / OnHold) ─────────────────────────────────────────

function FreeLine({ line, busy, form, setForm, selected, onToggleSelect, run }: {
  line: SupplierOrderLineDto
  busy: boolean
  form: string | null
  setForm: (f: string | null) => void
  selected: boolean
  onToggleSelect: () => void
  run: (a: () => Promise<unknown>, ok: string) => Promise<void>
}) {
  const postponeKey = `line:${line.id}:postpone`

  return (
    <div className="rounded-md border p-1.5" data-testid={`planning-order-line-${line.id}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} disabled={busy}
          data-testid={`planning-order-line-select-${line.id}`} className="h-3.5 w-3.5 flex-none" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium">{line.title ?? line.description}</div>
          {line.title && supplierLineDetailOf(line)
            && <div className="truncate text-xs text-muted-foreground">{supplierLineDetailOf(line)}</div>}
          {line.supplierName && <div className="truncate text-xs text-muted-foreground">{line.supplierName}</div>}
        </div>
        {line.quantity != null && (
          <span className="flex-none whitespace-nowrap text-[11px] text-muted-foreground" data-testid={`planning-order-line-qty-${line.id}`}>× {line.quantity}</span>
        )}
        {/* "À commander" pill omitted — redundant in the free-lines zone; OnHold shown for context */}
        {line.lineStatus !== 'ToOrder' && (
          <span className={cn('flex-none rounded px-1.5 py-0.5 text-[10px] font-medium', LINE_STATUS_CLASS[line.lineStatus])}>
            {LINE_STATUS_LABEL[line.lineStatus]}
          </span>
        )}
        {/* « Annuler la ligne » retiré du drawer (MS-14 item 3) — le registre est l'unique point
            d'ajout/retrait des lignes à commander. Le suivi (rupture stock sur commande envoyée)
            garde son annulation dans l'OrderCard. */}
      </div>

      {line.lineStatus === 'OnHold' && line.holdKind && (
        <div className="mt-1 flex items-center gap-1.5 pl-5 text-[11px] text-amber-700">
          <span>
            {HOLD_KIND_LABEL[line.holdKind]}
            {line.holdUntil ? ` · jusqu'au ${frDate(line.holdUntil)}` : ''}
            {line.holdNote ? ` · ${line.holdNote}` : ''}
          </span>
          <button type="button" disabled={busy} data-testid={`planning-order-line-postpone-${line.id}`}
            onClick={() => setForm(form === postponeKey ? null : postponeKey)}
            className="flex-none rounded border px-1 text-[10px] text-muted-foreground hover:bg-muted">Repousser</button>
        </div>
      )}

      {form === postponeKey && <DateForm busy={busy} label="Nouvelle échéance" defaultDate={line.holdUntil ?? todayIso()} testId={`planning-order-line-postpone-form-${line.id}`}
        onCancel={() => setForm(null)}
        onSubmit={(date) => run(() => planningApi.postponeLineHold(line.id, date), 'Échéance déplacée')} />}
    </div>
  )
}

// ── Order card ───────────────────────────────────────────────────────────────

function OrderCard({ order, projectLines, busy, mutating, form, setForm, run, loadSuppliers, suppliers, poseStartIso, deliveryLeadDays, confirmDelayDays }: {
  order: SupplierOrderDto
  projectLines: SupplierOrderLineDto[]
  busy: boolean
  /** `busy` sans la lecture seule : ce qui ne doit se fermer que pendant un appel en cours, comme
   *  le menu ⋯ dont la seule entrée toujours disponible (« Historique ») est une lecture. */
  mutating: boolean
  form: string | null
  setForm: (f: string | null) => void
  run: (a: () => Promise<unknown>, ok: string) => Promise<void>
  loadSuppliers: () => Promise<SupplierDto[]>
  suppliers: SupplierDto[] | null
  poseStartIso: string | null
  deliveryLeadDays: number | null
  confirmDelayDays: number | null
}) {
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set())
  const [showMenu, setShowMenu] = useState(false)

  const otherCount = order.totalLineCount - projectLines.length
  const key = (action: string) => `order:${order.id}:${action}`
  const supplier = suppliers?.find((s) => s.id === order.supplierId) ?? null

  // V102: period derived from lines (not order)
  const effectivePeriodInfo = orderEffectivePeriod(projectLines)
  const deadline = orderNextDeadline(order, projectLines)
  const isDeadlineOverdue = deadline !== null && deadline.date < todayIso()
  // Pre-fill confirmedPeriod with the desired period of the first selected Assigned line (B1-6)
  const selectedDesiredPeriod = projectLines.find((l) => selectedLines.has(l.id) && l.desiredDeliveryPeriod)?.desiredDeliveryPeriod ?? null

  // Bulk action eligibility by order state
  // Sent: confirm Assigned lines; Confirmed: receive/reschedule Confirmed lines
  const eligibleForBulk = order.status === 'Sent'
    ? projectLines.filter((l) => l.lineStatus === 'Assigned')
    : order.status === 'Confirmed'
      ? projectLines.filter((l) => l.lineStatus === 'Confirmed')
      : []
  const canBulkAct = eligibleForBulk.length > 0
  const allLinesSelected = canBulkAct && selectedLines.size === eligibleForBulk.length

  const toggleLineSelect = (id: string) =>
    setSelectedLines((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next })

  return (
    <div className="rounded-md border bg-card p-2" data-testid={`planning-supplier-order-${order.id}`}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold">{order.supplierName}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {order.supplierOrderNumber && <span>n° {order.supplierOrderNumber} · </span>}
            {order.mode && <span>{MODE_LABEL[order.mode]} · </span>}
            {effectivePeriodInfo && (
              <span>livraison {formatPeriod(effectivePeriodInfo.period)}{effectivePeriodInfo.isConfirmed ? '' : ' (souhaitée)'}</span>
            )}
          </div>
        </div>
        {order.totalProjectCount > 1 && (
          <span className="flex-none rounded bg-muted px-1.5 py-0.5 text-[9.5px] text-muted-foreground" data-testid={`planning-order-multi-${order.id}`}>
            ⛓ {order.totalLineCount} art. · {order.totalProjectCount} chantiers
          </span>
        )}
        <span className={cn('flex-none rounded px-1.5 py-0.5 text-[10px] font-medium', ORDER_STATUS_CLASS[order.status])}>
          {ORDER_STATUS_LABEL[order.status]}
        </span>
        {/* ⋯ secondary actions: Historique, Supprimer */}
        <div className="relative flex-none">
          <button type="button" disabled={mutating} data-testid={`planning-order-menu-${order.id}`}
            onClick={() => setShowMenu((s) => !s)}
            className="rounded px-1 py-0.5 text-[13px] leading-none text-muted-foreground hover:bg-muted">⋯</button>
          {showMenu && (
            <div className="absolute right-0 top-full z-10 min-w-[152px] rounded-md border bg-card py-1 shadow-md">
              {order.status !== 'Sent' && (
                <button type="button" data-testid={`planning-order-history-menu-${order.id}`}
                  onClick={() => { setShowMenu(false); setForm(form === key('history') ? null : key('history')) }}
                  className="w-full px-3 py-1.5 text-left text-[11px] hover:bg-muted">Historique</button>
              )}
              {order.status === 'Draft' && (
                <button type="button" disabled={busy} data-testid={`planning-order-delete-${order.id}`}
                  onClick={() => { setShowMenu(false); void run(() => planningApi.deleteOrderDraft(order.id), 'Brouillon supprimé') }}
                  className="w-full px-3 py-1.5 text-left text-[11px] text-destructive hover:bg-muted">Supprimer</button>
              )}
            </div>
          )}
        </div>
      </div>

      {deadline && !isDeadlineOverdue && (
        <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`planning-order-deadline-${order.id}`}>
          ⏱ {deadline.label} — <span className="font-medium text-foreground">{frDate(deadline.date)}</span>
        </div>
      )}
      {/* C5: Sent + confirmation deadline passed */}
      {isDeadlineOverdue && order.status === 'Sent' && (
        <p className="mt-1 text-[11px] font-medium text-amber-600" data-testid={`planning-order-confirm-overdue-${order.id}`}>
          ⚠ Confirmation en retard — attendue le {frDate(deadline!.date)}
        </p>
      )}
      {/* C6: Confirmed + delivery period past today */}
      {isDeadlineOverdue && order.status === 'Confirmed' && (
        <p className="mt-1 text-[11px] font-medium text-red-600" data-testid={`planning-order-delivery-overdue-${order.id}`}>
          ⚠ Livraison en retard — attendue pour le {frDate(deadline!.date)}
        </p>
      )}
      {/* C7: delivery period after/too close to pose date */}
      {effectivePeriodInfo && order.status !== 'Closed' && (
        <DeliveryPoseWarning period={effectivePeriodInfo.period} poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} testId={`planning-order-pose-risk-${order.id}`} />
      )}

      {/* Lines of THIS project */}
      <div className="mt-1.5 space-y-1 border-t pt-1.5">
        {/* ── Bulk action row ── */}
        {canBulkAct && (
          <div className="flex items-center gap-2 pb-1 text-[11px]">
            <label className="flex cursor-pointer items-center gap-1.5 text-muted-foreground" data-testid={`planning-order-select-all-${order.id}`}>
              <input type="checkbox" className="h-3.5 w-3.5" disabled={busy}
                checked={allLinesSelected}
                onChange={() => {
                  if (allLinesSelected) setSelectedLines(new Set())
                  else setSelectedLines(new Set(eligibleForBulk.map((l) => l.id)))
                }} />
              Tout
            </label>
            {/* Sent: Confirmer */}
            {order.status === 'Sent' && (
              <button type="button" disabled={busy || selectedLines.size === 0}
                data-testid={`planning-order-bulk-confirm-${order.id}`}
                onClick={() => setForm(form === key('bulk-confirm') ? null : key('bulk-confirm'))}
                className={cn('rounded border px-2 py-0.5 font-semibold transition-colors',
                  selectedLines.size > 0
                    ? 'border-slate-300 bg-slate-700 text-white hover:bg-slate-800'
                    : 'border-slate-200 text-slate-300')}>
                Confirmer{selectedLines.size > 0 ? ` (${selectedLines.size})` : ''}
              </button>
            )}
            {/* Confirmed: Marquer reçu */}
            {order.status === 'Confirmed' && (
              <button type="button" disabled={busy || selectedLines.size === 0}
                data-testid={`planning-order-bulk-receive-${order.id}`}
                onClick={() => setForm(form === key('bulk-receive') ? null : key('bulk-receive'))}
                className={cn('rounded border px-2 py-0.5 font-semibold transition-colors',
                  selectedLines.size > 0
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    : 'border-slate-200 text-slate-300')}>
                Marquer reçu{selectedLines.size > 0 ? ` (${selectedLines.size})` : ''}
              </button>
            )}
            {/* Confirmed: Reprogrammer */}
            {order.status === 'Confirmed' && (
              <button type="button" disabled={busy || selectedLines.size === 0}
                data-testid={`planning-order-bulk-reschedule-${order.id}`}
                onClick={() => setForm(form === key('bulk-reschedule') ? null : key('bulk-reschedule'))}
                className={cn('rounded border px-2 py-0.5 transition-colors',
                  selectedLines.size > 0
                    ? 'border-slate-200 text-muted-foreground hover:bg-muted'
                    : 'border-slate-200 text-slate-300')}>
                Reprogrammer{selectedLines.size > 0 ? ` (${selectedLines.size})` : ''}
              </button>
            )}
            <button type="button" disabled={busy || selectedLines.size === 0}
              data-testid={`planning-order-bulk-cancel-${order.id}`}
              onClick={() => setForm(form === key('bulk-cancel') ? null : key('bulk-cancel'))}
              className={cn('rounded border px-2 py-0.5 transition-colors',
                selectedLines.size > 0
                  ? 'border-slate-200 text-muted-foreground hover:text-destructive'
                  : 'border-slate-200 text-slate-300')}>
              Annuler{selectedLines.size > 0 ? ` (${selectedLines.size})` : ''}
            </button>
          </div>
        )}

        {/* ── Bulk confirm form ── */}
        {form === key('bulk-confirm') && selectedLines.size > 0 && (
          <BulkConfirmForm busy={busy} lineCount={selectedLines.size}
            poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays}
            initialDesiredPeriod={selectedDesiredPeriod}
            onCancel={() => setForm(null)}
            onSubmit={(confirmedPeriod, supplierOrderNumber) => {
              const ids = [...selectedLines]
              setSelectedLines(new Set())
              return run(
                () => planningApi.bulkConfirmLines({ lineIds: ids, confirmedPeriod, confirmedAt: todayIso(), supplierOrderNumber }),
                `${ids.length} ligne${ids.length > 1 ? 's' : ''} confirmée${ids.length > 1 ? 's' : ''}`
              )
            }} />
        )}

        {/* ── Bulk receive date form ── */}
        {form === key('bulk-receive') && selectedLines.size > 0 && (
          <DateForm busy={busy} label="Reçu le" defaultDate={todayIso()}
            testId={`planning-order-bulk-receive-form-${order.id}`}
            onCancel={() => setForm(null)}
            onSubmit={(date) => {
              const ids = [...selectedLines]
              setSelectedLines(new Set())
              return run(
                () => Promise.all(ids.map((id) => planningApi.receiveSupplierLine(id, date))),
                `${ids.length} réception${ids.length > 1 ? 's' : ''} enregistrée${ids.length > 1 ? 's' : ''}`
              )
            }} />
        )}

        {/* ── Bulk reschedule form ── */}
        {form === key('bulk-reschedule') && selectedLines.size > 0 && (
          <PeriodForm busy={busy} label="Nouvelle période de livraison"
            initial={effectivePeriodInfo?.period ?? null}
            testId={`planning-order-bulk-reschedule-form-${order.id}`}
            onCancel={() => setForm(null)}
            onSubmit={(newPeriod) => {
              const ids = [...selectedLines]
              setSelectedLines(new Set())
              return run(
                () => planningApi.bulkRescheduleLines({ lineIds: ids, newPeriod }),
                `${ids.length} période${ids.length > 1 ? 's' : ''} mise${ids.length > 1 ? 's' : ''} à jour`
              )
            }} />
        )}

        {/* ── Bulk cancel reason form ── */}
        {form === key('bulk-cancel') && selectedLines.size > 0 && (
          <ReasonForm busy={busy} label="Motif d'annulation"
            submitLabel={`Annuler ${selectedLines.size} ligne${selectedLines.size > 1 ? 's' : ''}`}
            testId={`planning-order-bulk-cancel-form-${order.id}`}
            onCancel={() => setForm(null)}
            onSubmit={(reason) => {
              const ids = [...selectedLines]
              setSelectedLines(new Set())
              return run(
                () => Promise.all(ids.map((id) => planningApi.cancelSupplierLine(id, { date: todayIso(), reason }))),
                `${ids.length} ligne${ids.length > 1 ? 's' : ''} annulée${ids.length > 1 ? 's' : ''}`
              )
            }} />
        )}

        {projectLines.map((l) => (
          <div key={l.id} data-testid={`planning-order-line-${l.id}`}>
            <div className="flex items-center gap-2">
              {/* Checkbox for eligible lines when bulk actions are available */}
              {canBulkAct && eligibleForBulk.some((e) => e.id === l.id) && (
                <input type="checkbox" checked={selectedLines.has(l.id)} onChange={() => toggleLineSelect(l.id)}
                  disabled={busy}
                  className="h-3.5 w-3.5 flex-none" data-testid={`planning-order-line-select-${l.id}`} />
              )}
              <div className={cn('min-w-0 flex-1 truncate text-xs', l.lineStatus === 'Cancelled' && 'text-muted-foreground line-through')}>
                {supplierLineTextOf(l)}
                {l.lineStatus === 'Cancelled' && l.cancelReason && <span className="ml-1 text-[10px]">({l.cancelReason})</span>}
              </div>
              {l.quantity != null && (
                <span className="flex-none whitespace-nowrap text-[11px] text-muted-foreground" data-testid={`planning-order-line-qty-${l.id}`}>× {l.quantity}</span>
              )}
              {/* Status pill hidden for eligibleForBulk lines in action mode (same status shared) */}
              {!(canBulkAct && eligibleForBulk.some((e) => e.id === l.id)) && (
                <span className={cn('flex-none rounded px-1.5 py-0.5 text-[10px]', LINE_STATUS_CLASS[l.lineStatus])}>
                  {LINE_STATUS_LABEL[l.lineStatus]}
                </span>
              )}
              {/* Draft: detach button per-line */}
              {order.status === 'Draft' && l.lineStatus === 'Assigned' && (
                <button type="button" disabled={busy} title="Retirer de la commande" data-testid={`planning-order-line-detach-${l.id}`}
                  onClick={() => void run(() => planningApi.detachOrderLine(order.id, l.id), 'Ligne retirée')}
                  className="flex-none rounded px-1 text-[11px] text-muted-foreground hover:text-destructive">✕</button>
              )}
            </div>
            {/* V102: show per-line period info */}
            {(l.confirmedDeliveryPeriod ?? l.desiredDeliveryPeriod) && (
              <div className="mt-0.5 pl-3 text-[10px] text-muted-foreground">
                {l.confirmedDeliveryPeriod
                  ? `Confirmée : ${formatPeriod(l.confirmedDeliveryPeriod)}`
                  : `Souhaitée : ${formatPeriod(l.desiredDeliveryPeriod!)}`}
                {l.confirmationExpectedBy && ` · conf. avant ${frDate(l.confirmationExpectedBy)}`}
              </div>
            )}
          </div>
        ))}
        {otherCount > 0 && (
          <div className="text-[11px] italic text-muted-foreground" data-testid={`planning-order-others-${order.id}`}>
            + {otherCount} ligne{otherCount > 1 ? 's' : ''} d'autres chantiers ({order.totalProjectCount} chantiers groupés)
          </div>
        )}
      </div>

      {/* Primary status-transition actions */}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {order.status === 'Draft' && (
          <button type="button" disabled={busy} data-testid={`planning-order-send-${order.id}`}
            onClick={() => { void loadSuppliers(); setForm(form === key('send') ? null : key('send')) }}
            className="rounded bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground">
            Commande envoyée
          </button>
        )}
      </div>

      {form === key('send') && (supplier
        ? <SendForm busy={busy} supplier={supplier} onCancel={() => setForm(null)}
            poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} confirmDelayDays={confirmDelayDays}
            onSubmit={(mode, desiredPeriod, sentDate, confirmationExpectedBy) =>
              run(() => planningApi.markSentOrder(order.id, { mode, desiredPeriod, sentDate, confirmationExpectedBy }), 'Commande envoyée')} />
        : <p className="mt-1.5 text-[11px] text-muted-foreground">Chargement du fournisseur…</p>)}
      {form === key('history') && <FollowUpThread orderId={order.id} />}
    </div>
  )
}

// ── Follow-up thread (MS-04) — the vacation-handover history ─────────────────

const ENTRY_ICON: Record<string, string> = {
  StatusChange: '◆', DateChange: '⏱', Comment: '💬', Relance: '📞',
  ReminderCreated: '⏰', ReminderCompleted: '✓', ReminderRescheduled: '⏰',
  LineAttached: '＋', LineDetached: '−', LineCancelled: '✕', LineReceived: '📦',
}

function FollowUpThread({ orderId }: { orderId: string }) {
  const [entries, setEntries] = useState<SupplierOrderEntryDto[] | null>(null)
  const [comment, setComment] = useState('')
  const [posting, setPosting] = useState(false)

  useEffect(() => {
    let live = true
    void planningApi.getOrderEntries(orderId).then((e) => { if (live) setEntries(e) })
    return () => { live = false }
  }, [orderId])

  // The thread stays OPEN after a comment (the user must see it land) and the text survives a
  // failure — so this does not go through run() which closes the active form (review N1).
  const postComment = async () => {
    setPosting(true)
    try {
      await planningApi.commentOrder(orderId, comment.trim())
      setComment('')
      setEntries(await planningApi.getOrderEntries(orderId))
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Commentaire refusé') }
    finally { setPosting(false) }
  }

  return (
    <div className="mt-1.5 space-y-1 rounded-md border bg-muted/20 p-2" data-testid={`planning-order-thread-${orderId}`}>
      {entries === null && <p className="text-[11px] text-muted-foreground">Chargement…</p>}
      {entries?.length === 0 && <p className="text-[11px] text-muted-foreground">Aucun évènement</p>}
      {entries?.map((e) => (
        <div key={e.id} className="flex items-baseline gap-1.5 text-[11px]">
          <span className="flex-none" aria-hidden>{ENTRY_ICON[e.kind] ?? '·'}</span>
          <span className="flex-none text-muted-foreground">{new Date(e.occurredAt).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })}</span>
          <span className="min-w-0 flex-1 break-words">{e.payload ?? e.kind}</span>
        </div>
      ))}
      <div className="flex gap-1 pt-1">
        <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire…"
          className="min-w-0 flex-1 rounded border px-2 py-1 text-xs" data-testid={`planning-order-comment-${orderId}`} />
        <button type="button" disabled={posting || !comment.trim()} data-testid={`planning-order-comment-submit-${orderId}`}
          onClick={() => void postComment()}
          className="flex-none rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">OK</button>
      </div>
    </div>
  )
}


// ── UX r2 — bulk toolbar + gesture forms (MS-11/12) ──────────────────────────

function ToolbarBtn({ label, primary = false, active, disabled = false, testId, onClick }: {
  label: string
  primary?: boolean
  active: boolean
  disabled?: boolean
  testId: string
  onClick: () => void
}) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={active} disabled={disabled}
      className={cn('rounded px-2 py-0.5 text-[10.5px] font-semibold disabled:opacity-40',
        primary ? 'bg-primary text-primary-foreground' : 'border border-border bg-background text-foreground',
        active && 'ring-2 ring-primary/40')}>
      {label}
    </button>
  )
}

/** Supplier picker with inline creation — the cache callback keeps the drawer coherent (review fix). */
function SupplierSelect({ busy, value, loadSuppliers, onSupplierCreated, onChange }: {
  busy: boolean
  value: string
  loadSuppliers: () => Promise<SupplierDto[]>
  onSupplierCreated: (s: SupplierDto) => void
  onChange: (supplierId: string, supplier: SupplierDto | null) => void
}) {
  const [list, setList] = useState<SupplierDto[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [creatingBusy, setCreatingBusy] = useState(false)
  const [newName, setNewName] = useState('')
  const [newModes, setNewModes] = useState<SupplierOrderMode[]>(['Email'])

  useEffect(() => { void loadSuppliers().then(setList) }, [loadSuppliers])

  const createInline = async () => {
    if (!newName.trim() || creatingBusy || newModes.length === 0) return
    setCreatingBusy(true)
    try {
      const created = await planningApi.createSupplier({ name: newName.trim(), allowedModes: newModes, expectsConfirmation: true })
      setList((l) => [...(l ?? []), created].sort((a, b) => a.name.localeCompare(b.name)))
      onSupplierCreated(created)
      onChange(created.id, created)
      setCreating(false)
      setNewName('')
      setNewModes(['Email'])
    } finally { setCreatingBusy(false) }
  }

  if (creating) {
    return (
      <div className="space-y-1.5">
        <div className="flex gap-1">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du fournisseur" className="flex-1 rounded border px-2 py-1 text-xs" data-testid="planning-supplier-new-name" autoFocus />
          <button type="button" disabled={busy || creatingBusy || !newName.trim() || newModes.length === 0} data-testid="planning-supplier-new-submit"
            onClick={() => void createInline().catch((e) => toast.error(e instanceof Error ? e.message : 'Création échouée'))}
            className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50">Créer</button>
          <button type="button" onClick={() => { setCreating(false); setNewName(''); setNewModes(['Email']) }} data-testid="planning-supplier-new-cancel"
            className="rounded border px-2 py-1 text-xs text-muted-foreground">✕</button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">Mode :</span>
          {SUPPLIER_MODES.map((m) => (
            <label key={m} className="flex cursor-pointer items-center gap-1">
              <input type="checkbox" className="h-3.5 w-3.5"
                checked={newModes.includes(m)}
                onChange={() => setNewModes((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m])} />
              {MODE_LABEL[m]}
            </label>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-1">
      <select value={value} onChange={(e) => onChange(e.target.value, list?.find((s) => s.id === e.target.value) ?? null)}
        className="flex-1 rounded border px-2 py-1 text-xs" data-testid="planning-order-create-supplier">
        <option value="">Choisir un fournisseur…</option>
        {(list ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <button type="button" onClick={() => setCreating(true)} data-testid="planning-supplier-new" className="rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-muted">＋ Nouveau</button>
    </div>
  )
}

/**
 * « Commandé… » — the UX r2 flagship gesture: supplier (+ inline create), mode, desired period,
 * sent date, optional n° (portal flow → instantly Confirmed), inline C7 warning, and the ⛓ panel:
 * pending articles of OTHER projects for the same supplier (MS-12). The submit button always
 * announces its blast radius (« Commander N articles · M chantiers »).
 */
function OrderGestureForm({ busy, projectId, lineCount, loadSuppliers, onSupplierCreated, poseStartIso, deliveryLeadDays, confirmDelayDays, onCancel, onSubmit }: {
  busy: boolean
  projectId: string
  lineCount: number
  loadSuppliers: () => Promise<SupplierDto[]>
  onSupplierCreated: (s: SupplierDto) => void
  poseStartIso: string | null
  deliveryLeadDays: number | null
  confirmDelayDays: number | null
  onCancel: () => void
  onSubmit: (body: {
    supplierId: string
    mode: SupplierOrderMode
    desiredPeriod: PeriodDto
    sentDate: string
    confirmationExpectedBy: string | null
  }, extraLineIds: string[]) => Promise<void>
}) {
  const [supplierId, setSupplierId] = useState('')
  const [supplier, setSupplier] = useState<SupplierDto | null>(null)
  const [mode, setMode] = useState<SupplierOrderMode>('Email')
  const [period, setPeriod] = useState<PeriodDto>(periodFromInput('Week', addWorkingDays(todayIso(), 10)))
  const [deadline, setDeadline] = useState(addWorkingDays(todayIso(), confirmDelayDays ?? 5))
  const [candidates, setCandidates] = useState<BoardLineDto[]>([])
  const [checkedCandidates, setCheckedCandidates] = useState<Set<string>>(new Set())
  const [loadingCandidates, setLoadingCandidates] = useState(false)

  const expectsConfirmation = supplier?.expectsConfirmation ?? true

  const pickSupplier = (id: string, s: SupplierDto | null) => {
    setSupplierId(id)
    setSupplier(s)
    setMode(s?.allowedModes?.[0] ?? 'Email')
    // UX r2 arbitrage: supplier delay > org KV > 5.
    setDeadline(addWorkingDays(todayIso(), s?.defaultConfirmationDelayDays ?? confirmDelayDays ?? 5))
    setCandidates([])
    setCheckedCandidates(new Set())
    setLoadingCandidates(false)
    if (id) {
      setLoadingCandidates(true)
      void planningApi.getGroupingCandidates(id, projectId)
        .then((cs) => {
          setCandidates(cs)
          // Grouping holds for this supplier are pre-checked — being ordered together is their
          // raison d'être. Plain ToOrder stays unchecked (intent unknown).
          setCheckedCandidates(new Set(cs.filter((c) => c.holdKind === 'Grouping').map((c) => c.id)))
        })
        .catch(() => setCandidates([]))
        .finally(() => setLoadingCandidates(false))
    }
  }

  const totalArticles = lineCount + checkedCandidates.size
  // Blast radius: this project + every distinct OTHER project among the checked candidates.
  const projectCount = 1 + new Set(candidates.filter((c) => checkedCandidates.has(c.id)).map((c) => c.projectId)).size

  // Group candidates by project for readable display.
  const candidatesByProject = candidates.reduce<Map<string, { clientName: string; items: BoardLineDto[] }>>(
    (acc, c) => {
      const g = acc.get(c.projectId) ?? { clientName: c.clientName, items: [] }
      g.items.push(c)
      return acc.set(c.projectId, g)
    },
    new Map()
  )

  return (
    <div className="space-y-1.5 rounded-md border border-slate-300 bg-muted/60 p-2" data-testid="planning-bulk-order-form">
      <div className="text-xs font-semibold">Marquer commandé — {lineCount} article{lineCount > 1 ? 's' : ''}</div>
      <SupplierSelect busy={busy} value={supplierId} loadSuppliers={loadSuppliers} onSupplierCreated={onSupplierCreated} onChange={pickSupplier} />
      {supplierId && (
        <>
          <div className="flex flex-wrap items-center gap-1 text-[11px]">
            <span className="text-muted-foreground">Mode :</span>
            {SUPPLIER_MODES.map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} data-testid={`planning-bulk-order-mode-${m}`}
                className={cn('rounded border px-1.5 py-0.5', mode === m ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground">Date de livraison souhaitée :</div>
          <PeriodInput value={period} onChange={setPeriod} testId="planning-bulk-order-period" />
          <DeliveryPoseWarning period={period} poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} testId="planning-bulk-order-pose-warn" />
          {expectsConfirmation && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Confirmation attendue avant le</span>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="planning-bulk-order-deadline" />
            </div>
          )}
          {!expectsConfirmation && <div className="text-[11px] italic text-muted-foreground">Ce fournisseur ne confirme pas les commandes.</div>}

          {/* ── MS-12 — the ⛓ panel: pending articles of OTHER projects ── */}
          {loadingCandidates && (
            <div className="text-[11px] italic text-muted-foreground" data-testid="planning-bulk-order-candidates-loading">Recherche d'articles à grouper…</div>
          )}
          {!loadingCandidates && candidates.length === 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="planning-bulk-order-candidates-empty">Aucun autre article en attente pour ce fournisseur.</div>
          )}
          {!loadingCandidates && candidates.length > 0 && (
            <div className="space-y-1 rounded-md bg-amber-50 p-1.5" data-testid="planning-bulk-order-candidates">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold text-amber-800">
                  ⛓ {candidates.length} autre{candidates.length > 1 ? 's' : ''} article{candidates.length > 1 ? 's' : ''} attend{candidates.length > 1 ? 'ent' : ''} {supplier?.name}
                  {candidatesByProject.size > 1 && ` · ${candidatesByProject.size} chantiers`} :
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="planning-bulk-order-candidates-all"
                    onClick={() => setCheckedCandidates(new Set(candidates.map((c) => c.id)))}
                    className="text-[10.5px] text-amber-800 underline">tout ajouter ({candidates.length})</button>
                  {checkedCandidates.size > 0 && (
                    <button type="button" data-testid="planning-bulk-order-candidates-none"
                      onClick={() => setCheckedCandidates(new Set())}
                      className="text-[10.5px] text-muted-foreground underline">tout retirer</button>
                  )}
                </div>
              </div>
              {[...candidatesByProject.entries()].map(([pid, { clientName, items }]) => (
                <div key={pid}>
                  {candidatesByProject.size > 1 && (
                    <div className="px-1 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700/70">{clientName}</div>
                  )}
                  {items.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 rounded bg-white px-1.5 py-1 text-[11px]" data-testid={`planning-bulk-order-candidate-${c.id}`}>
                      <input type="checkbox" checked={checkedCandidates.has(c.id)}
                        onChange={() => setCheckedCandidates((s) => {
                          const next = new Set(s)
                          if (next.has(c.id)) next.delete(c.id); else next.add(c.id)
                          return next
                        })}
                        className="h-3.5 w-3.5 flex-none" />
                      <span className="min-w-0 flex-1 truncate">
                        {supplierLineTextOf(c)}
                        {candidatesByProject.size === 1 && <i className="ml-1 text-muted-foreground">— {c.clientName}</i>}
                      </span>
                      <span className={cn('flex-none rounded px-1.5 py-0.5 text-[9.5px] font-medium', LINE_STATUS_CLASS[c.lineStatus])}>
                        {c.holdKind === 'Grouping' && c.holdUntil ? `groupage · ${frDate(c.holdUntil)}` : LINE_STATUS_LABEL[c.lineStatus]}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="planning-bulk-order-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" data-testid="planning-bulk-order-submit"
          disabled={busy || !supplierId || (expectsConfirmation && !deadline)}
          onClick={() => void onSubmit({
            supplierId, mode, desiredPeriod: period, sentDate: todayIso(),
            confirmationExpectedBy: expectsConfirmation ? deadline : null,
          }, [...checkedCandidates])}
          className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-50">
          {busy && <Spinner />}Commander {totalArticles} article{totalArticles > 1 ? 's' : ''}{checkedCandidates.size > 0 ? ` · ${projectCount} chantiers` : ''}
        </button>
      </div>
    </div>
  )
}

/** « Attendre pour grouper… » — supplier + the date the order will be placed (the hold deadline).
 *  Lines with this hold are pre-checked in the ⛓ panel when OrderGestureForm picks this supplier. */
function GroupHoldForm({ busy, lineCount, loadSuppliers, onSupplierCreated, onCancel, onSubmit }: {
  busy: boolean
  lineCount: number
  loadSuppliers: () => Promise<SupplierDto[]>
  onSupplierCreated: (s: SupplierDto) => void
  onCancel: () => void
  onSubmit: (supplierId: string, until: string, note: string) => Promise<void>
}) {
  const [supplierId, setSupplierId] = useState('')
  const [supplierName, setSupplierName] = useState('')
  const [until, setUntil] = useState(addWorkingDays(todayIso(), 5))
  const [note, setNote] = useState('')

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid="planning-bulk-group-form">
      <div className="text-xs font-semibold">
        Grouper {lineCount} article{lineCount > 1 ? 's' : ''} — attendre la commande fournisseur
      </div>
      <SupplierSelect busy={busy} value={supplierId} loadSuppliers={loadSuppliers} onSupplierCreated={onSupplierCreated}
        onChange={(id, s) => { setSupplierId(id); setSupplierName(s?.name ?? '') }} />
      {supplierId && (
        <p className="text-[10.5px] text-amber-700">
          Ces articles apparaîtront pré-cochés dans la prochaine commande {supplierName || 'ce fournisseur'}.
        </p>
      )}
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="text-muted-foreground">Commande prévue le</span>
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="planning-bulk-group-until" />
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)}
        placeholder={`Contexte (optionnel — ex: tournée ${supplierName || 'fournisseur'} de mercredi)`}
        className="w-full rounded border px-2 py-1 text-xs" data-testid="planning-bulk-group-note" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="planning-bulk-group-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy || !supplierId || !until} data-testid="planning-bulk-group-submit"
          onClick={() => void onSubmit(supplierId, until, note.trim())}
          className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50">
          {busy && <Spinner />}Grouper chez {supplierName || 'ce fournisseur'}
        </button>
      </div>
    </div>
  )
}

/** « Info… » — who we're waiting on (vendeur or client) + relance deadline (mandatory, no-blind-spot). */
function InfoHoldForm({ busy, onCancel, onSubmit }: {
  busy: boolean
  onCancel: () => void
  onSubmit: (until: string, note: string) => Promise<void>
}) {
  const [context, setContext] = useState<'vendeur' | 'client'>('vendeur')
  const [until, setUntil] = useState(addWorkingDays(todayIso(), 3))
  const [note, setNote] = useState('')
  const groupName = useId()

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid="planning-bulk-info-form">
      <div className="flex items-center gap-3 text-xs font-semibold">
        <span>Complément d'info — attendu du</span>
        <label className="flex cursor-pointer items-center gap-1">
          <input type="radio" name={groupName} value="vendeur" checked={context === 'vendeur'} onChange={() => setContext('vendeur')} data-testid="planning-bulk-info-context-vendeur" />
          vendeur
        </label>
        <label className="flex cursor-pointer items-center gap-1">
          <input type="radio" name={groupName} value="client" checked={context === 'client'} onChange={() => setContext('client')} data-testid="planning-bulk-info-context-client" />
          client
        </label>
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Contexte (obligatoire — ex: teinte du plan à confirmer)"
        className="w-full resize-none rounded border px-2 py-1 text-xs" data-testid="planning-bulk-info-note" autoFocus />
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="text-muted-foreground">Relancer si pas de réponse le</span>
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="planning-bulk-info-until" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="planning-bulk-info-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy || !until || !note.trim()} data-testid="planning-bulk-info-submit"
          onClick={() => void onSubmit(until, `${context === 'vendeur' ? 'Vendeur' : 'Client'} — ${note.trim()}`)}
          className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50">
          {busy && <Spinner />}Enregistrer
        </button>
      </div>
    </div>
  )
}

/**
 * Inline C7 warning (« cohérence amont », mirror of the métré M2 fiche warning): the margin shown
 * here computes the EXACT same working-days number as the Suivi alert — never a surprise later.
 */
function DeliveryPoseWarning({ period, poseStartIso, deliveryLeadDays, testId }: {
  period: PeriodDto
  poseStartIso: string | null
  deliveryLeadDays: number | null
  testId: string
}) {
  if (!poseStartIso || deliveryLeadDays === null) return null
  // BE mirror: margin = WorkingDays.Between(periodEnd + 1 day, poseDay).
  const dayAfterDelivery = new Date(`${period.end}T00:00:00Z`)
  dayAfterDelivery.setUTCDate(dayAfterDelivery.getUTCDate() + 1)
  const margin = workingDaysBetween(dayAfterDelivery.toISOString().slice(0, 10), poseStartIso)
  if (period.end >= poseStartIso) {
    return <p className="text-[11px] font-medium text-red-600" data-testid={testId}>⚠ Livraison le jour de la pose du {frDate(poseStartIso)} ou après</p>
  }
  if (margin < deliveryLeadDays) {
    return <p className="text-[11px] font-medium text-amber-600" data-testid={testId}>⚠ Marge de {margin} j ouvrables avant la pose du {frDate(poseStartIso)} — minimum {deliveryLeadDays}</p>
  }
  return null
}

function SendForm({ busy, supplier, poseStartIso, deliveryLeadDays, confirmDelayDays, onCancel, onSubmit }: {
  busy: boolean
  supplier: SupplierDto | null
  poseStartIso: string | null
  deliveryLeadDays: number | null
  confirmDelayDays: number | null
  onCancel: () => void
  onSubmit: (mode: SupplierOrderMode, desiredPeriod: PeriodDto, sentDate: string, confirmationExpectedBy: string | null) => Promise<void>
}) {
  const [mode, setMode] = useState<SupplierOrderMode>(supplier?.allowedModes?.[0] ?? 'Email')
  const [period, setPeriod] = useState<PeriodDto>(periodFromInput('Week', addWorkingDays(todayIso(), 10)))
  const expectsConfirmation = supplier?.expectsConfirmation ?? true
  // UX r2 arbitrage: supplier delay > org KV (SupplierConfirmDelayDays) > 5.
  const [deadline, setDeadline] = useState(addWorkingDays(todayIso(), supplier?.defaultConfirmationDelayDays ?? confirmDelayDays ?? 5))

  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid="planning-order-send-form">
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        <span className="text-muted-foreground">Mode :</span>
        {SUPPLIER_MODES.map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} data-testid={`planning-order-send-mode-${m}`}
            className={cn('rounded border px-1.5 py-0.5', mode === m ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">Date de livraison souhaitée :</div>
      <PeriodInput value={period} onChange={setPeriod} testId="planning-order-send-period" />
      <DeliveryPoseWarning period={period} poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} testId="planning-order-send-pose-warn" />
      {expectsConfirmation ? (
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">Confirmation attendue avant le</span>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid="planning-order-send-deadline" />
        </div>
      ) : (
        <div className="text-[11px] italic text-muted-foreground">Ce fournisseur ne confirme pas les commandes.</div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="planning-order-send-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy || (expectsConfirmation && !deadline)} data-testid="planning-order-send-submit"
          onClick={() => void onSubmit(mode, period, todayIso(), expectsConfirmation ? deadline : null)}
          className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-50">
          {busy && <Spinner />}Commande envoyée
        </button>
      </div>
    </div>
  )
}

function BulkConfirmForm({ busy, lineCount, poseStartIso, deliveryLeadDays, initialDesiredPeriod, onCancel, onSubmit }: {
  busy: boolean
  lineCount: number
  poseStartIso: string | null
  deliveryLeadDays: number | null
  initialDesiredPeriod: PeriodDto | null
  onCancel: () => void
  onSubmit: (confirmedPeriod: PeriodDto, supplierOrderNumber: string) => Promise<void>
}) {
  const [period, setPeriod] = useState<PeriodDto>(initialDesiredPeriod ?? periodFromInput('Week', addWorkingDays(todayIso(), 10)))
  const [number, setNumber] = useState('')

  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid="planning-order-bulk-confirm-form">
      <div className="text-[11px] font-medium">Confirmer {lineCount} ligne{lineCount > 1 ? 's' : ''}</div>
      {initialDesiredPeriod && (
        <div className="text-[11px] text-muted-foreground">
          Période souhaitée (commandée) : <span className="font-medium text-foreground">{formatPeriod(initialDesiredPeriod)}</span>
        </div>
      )}
      <div className="text-[11px] text-muted-foreground">Livraison confirmée par le fournisseur :</div>
      <PeriodInput value={period} onChange={setPeriod} testId="planning-order-bulk-confirm-period" />
      <DeliveryPoseWarning period={period} poseStartIso={poseStartIso} deliveryLeadDays={deliveryLeadDays} testId="planning-order-bulk-confirm-pose-warn" />
      <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Réf. / N° de commande fournisseur (obligatoire)" className="w-full rounded border px-2 py-1 text-xs" data-testid="planning-order-bulk-confirm-number" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="planning-order-bulk-confirm-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy || !number.trim()} data-testid="planning-order-bulk-confirm-submit"
          onClick={() => void onSubmit(period, number.trim())}
          className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-50">
          {busy && <Spinner />}Confirmer
        </button>
      </div>
    </div>
  )
}

function DateForm({ busy, label, defaultDate, testId, onCancel, onSubmit }: {
  busy: boolean
  label: string
  defaultDate: string
  testId: string
  onCancel: () => void
  onSubmit: (date: string) => Promise<void>
}) {
  const [date, setDate] = useState(defaultDate)
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border bg-muted/30 p-2 text-[11px]" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded border px-2 py-1 text-xs" data-testid={`${testId}-date`} />
      <button type="button" onClick={onCancel} data-testid={`${testId}-cancel`} className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
      <button type="button" disabled={busy || !date} onClick={() => void onSubmit(date)} data-testid={`${testId}-submit`}
        className="rounded bg-primary px-2.5 py-1 text-xs text-primary-foreground disabled:opacity-50">OK</button>
    </div>
  )
}

function ReasonForm({ busy, label, submitLabel, testId, onCancel, onSubmit }: {
  busy: boolean
  label: string
  submitLabel: string
  testId: string
  onCancel: () => void
  onSubmit: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  return (
    <div className="mt-1.5 space-y-1 rounded-md border bg-muted/30 p-2" data-testid={testId}>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`${label} (obligatoire)`} className="w-full rounded border px-2 py-1 text-xs" data-testid={`${testId}-reason`} autoFocus />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid={`${testId}-cancel`} className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy || !reason.trim()} onClick={() => void onSubmit(reason.trim())} data-testid={`${testId}-submit`}
          className="rounded bg-destructive px-2.5 py-1 text-xs text-destructive-foreground disabled:opacity-50">{submitLabel}</button>
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="mr-1.5 inline h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  )
}

function PeriodForm({ busy, label, initial, testId, onCancel, onSubmit }: {
  busy: boolean
  label: string
  initial: PeriodDto | null
  testId: string
  onCancel: () => void
  onSubmit: (p: PeriodDto) => Promise<void>
}) {
  const [period, setPeriod] = useState<PeriodDto>(initial ?? periodFromInput('Week', todayIso()))
  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border bg-muted/30 p-2" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground">{label} :</div>
      <PeriodInput value={period} onChange={setPeriod} testId={`${testId}-period`} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid={`${testId}-cancel`} className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={busy} onClick={() => void onSubmit(period)} data-testid={`${testId}-submit`}
          className="rounded bg-primary px-2.5 py-1 text-xs text-primary-foreground disabled:opacity-50">OK</button>
      </div>
    </div>
  )
}
